import Flash from "lib/flash"
import i18n from "lib/i18n"

const showMessage = (copyUrlEl, shareArticleMessageEl) => {
  copyUrlEl.classList.remove("opacity-100")
  copyUrlEl.classList.add("opacity-0")
  shareArticleMessageEl.classList.remove("opacity-0")
  shareArticleMessageEl.classList.add("!text-white", "opacity-100", "!w-full", "z-20")
}

const hideMessage = (copyUrlEl, shareArticleMessageEl) => {
  copyUrlEl.classList.remove("opacity-0")
  copyUrlEl.classList.add("opacity-100")
  shareArticleMessageEl.classList.remove("!text-white", "opacity-100", "!w-full", "z-20")
  shareArticleMessageEl.classList.add("opacity-0")
}

const handleClickLink = ({ element, fallbackHandler }) => {
  // iOS has some really weird behaviour with clipped text, so on iOS
  // we want to avoid the user interacting with the url in the clipped element
  // normally, just treat it like we click the copy url button.
  //
  // Other user agents and browsers can interact with the clipped text normally.
  if (navigator.platform.match(/iPhone|iPad/)) {
    fallbackHandler()
  } else if (element) {
    window.getSelection().selectAllChildren(element)
  }
}

const handleCopyUrl = async ({ linkEl, copyUrlEl, shareArticleMessageEl }) => {
  const url = new URL(linkEl.textContent.trim())
  const params = new URLSearchParams(url.search)
  params.set("utm_medium", "article_clipboard_share")
  params.set("utm_source", "theconversation.com")
  url.search = params.toString()

  try {
    await navigator.clipboard.writeText(url.href)
    if (copyUrlEl && shareArticleMessageEl) {
      setTimeout(showMessage.bind(undefined, copyUrlEl, shareArticleMessageEl), 150)
    }
    return true
  } catch (e) {
    // if for some reason the clipboard is blocked, give them a native prompt to copy from
    prompt(undefined, url.href)
    return false
  }
}

const createCopySuccessHandler = (copyUrlEl, shareArticleMessageEl, timeoutRef) => {
  return () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
      timeoutRef.current = null
    }

    timeoutRef.current = setTimeout(() => {
      hideMessage(copyUrlEl, shareArticleMessageEl)
      timeoutRef.current = null
    }, 2500)
  }
}

const handleNativeShare = async ({ linkEl, fallbackHandler, onFallbackSuccess }) => {
  if (navigator.share) {
    const url = new URL(linkEl.textContent.trim())
    const params = new URLSearchParams(url.search)
    params.set("utm_medium", "article_native_share")
    params.set("utm_source", "theconversation.com")
    url.search = params.toString()

    try {
      await navigator.share({
        url: url.href,
      })
    } catch (e) {
      // intentionally do nothing, some browsers seem to raise
      // an error here if the user cancels the share intent, we
      // protect against the share API not being available at
      // the start of this function
    }
  } else {
    // if the browser doesn't support native share for whatever reason
    // just fall back to copying to clipboard
    const success = await fallbackHandler()
    if (success && onFallbackSuccess) {
      onFallbackSuccess()
    }
  }
}

export default async function initShareButtons() {
  const linkEl = document.getElementById("share-article-url")

  // desktop elements
  const desktopDropdownEl = document.getElementById("share-article-dropdown-trigger-desktop")
  const desktopDropdownMenuEl = document.getElementById("share-dropdown-desktop")
  const desktopPrintEl = document.getElementById("share-article-print-desktop")

  if (desktopDropdownEl) desktopDropdownEl.classList.remove("hidden")
  if (desktopPrintEl) desktopPrintEl.classList.remove("hidden")

  // mobile elements
  const copyUrlMobileEl = document.getElementById("share-article-copy-url-mobile")
  const shareArticleMessageMobileEl = document.getElementById("share-article-message-mobile")
  const urlDisplayEl = document.getElementById("share-article-url-display-mobile")
  const mobileNativeShareEl = document.getElementById("share-article-native-mobile")

  // mobile event handlers
  if (copyUrlMobileEl && mobileNativeShareEl && shareArticleMessageMobileEl && linkEl) {
    const mobileTimeoutRef = { current: null }

    const boundHandleCopyUrl = handleCopyUrl.bind(undefined, {
      linkEl,
      copyUrlEl: copyUrlMobileEl,
      shareArticleMessageEl: shareArticleMessageMobileEl,
    })

    const mobileCopySuccessHandler = createCopySuccessHandler(
      copyUrlMobileEl,
      shareArticleMessageMobileEl,
      mobileTimeoutRef,
    )

    const boundHandleNativeShare = handleNativeShare.bind(undefined, {
      linkEl,
      fallbackHandler: boundHandleCopyUrl,
      onFallbackSuccess: mobileCopySuccessHandler,
    })

    const boundHandleClickLink = handleClickLink.bind(undefined, {
      element: urlDisplayEl,
      fallbackHandler: boundHandleCopyUrl,
    })

    // prevent flicker of unstyled content, this is an unfortunate side effect
    // of tailwind styles loading "late" which we should ideally fix
    copyUrlMobileEl.classList.add("transition-all", "duration-300")

    copyUrlMobileEl.addEventListener("click", async () => {
      const success = await boundHandleCopyUrl()
      if (success) {
        mobileCopySuccessHandler()
      }
    })

    mobileNativeShareEl.addEventListener("click", boundHandleNativeShare)

    if (urlDisplayEl) {
      urlDisplayEl.addEventListener("click", boundHandleClickLink)
    }
  }

  // desktop dropdown functionality
  if (desktopDropdownEl && desktopDropdownMenuEl) {
    desktopDropdownEl.addEventListener("click", (event) => {
      event.stopPropagation()

      // check if dropdown is currently hidden, because if its already shown we should not alter its position
      const isHidden = desktopDropdownMenuEl.classList.contains("hidden")

      if (isHidden) {
        const buttonRect = desktopDropdownEl.getBoundingClientRect()
        const viewportHeight = window.innerHeight
        const dropdownHeight = 340 // approximate height of dropdown el + spacing to button, probably not critcal enough to show and measure its height

        const spaceBelow = viewportHeight - buttonRect.bottom
        const spaceAbove = buttonRect.top

        // remove any existing positioning classes
        desktopDropdownMenuEl.classList.remove("bottom-full", "mb-2", "top-full", "mt-2")

        if (spaceBelow < dropdownHeight && spaceAbove > dropdownHeight) {
          // position above the button
          desktopDropdownMenuEl.classList.add("bottom-full", "mb-2")
        } else {
          // position below the button
          desktopDropdownMenuEl.classList.add("top-full", "mt-2")
        }
      }

      desktopDropdownMenuEl.classList.toggle("hidden")
    })
  }

  // handle copy link in dropdown
  if (desktopDropdownMenuEl && linkEl) {
    const copyLinkInDropdown = desktopDropdownMenuEl.querySelector("#share-dropdown-copy-link")
    if (copyLinkInDropdown) {
      copyLinkInDropdown.addEventListener("click", async (event) => {
        event.preventDefault()
        event.stopPropagation()

        // copy the URL directly
        const url = new URL(linkEl.textContent.trim())
        const params = new URLSearchParams(url.search)
        params.set("utm_medium", "article_clipboard_share")
        params.set("utm_source", "theconversation.com")
        url.search = params.toString()

        try {
          await navigator.clipboard.writeText(url.href)
          Flash.now("notice", i18n.t("social_media.notice.copied_link"))
        } catch (e) {
          // if for some reason the clipboard is blocked, give them a native prompt to copy from
          prompt(undefined, url.href)
        }

        desktopDropdownMenuEl.classList.add("hidden")
      })
    }
  }

  // close dropdown when clicking outside
  document.addEventListener("click", (event) => {
    const isDropdownClick = event.target.closest(
      "#share-dropdown-desktop, #share-article-dropdown-trigger-desktop",
    )
    if (!isDropdownClick && desktopDropdownMenuEl) {
      desktopDropdownMenuEl.classList.add("hidden")
    }
  })

  // close dropdown when pressing escape
  document.addEventListener("keydown", (event) => {
    if (event.key === "Escape" && desktopDropdownMenuEl) {
      desktopDropdownMenuEl.classList.add("hidden")
    }
  })

  if (desktopPrintEl) {
    desktopPrintEl.addEventListener("click", (event) => {
      event.preventDefault()
      window.print()
    })
  }
}
